import React, { useState, useEffect } from "react";
import styles from "./Projects.module.css";
import projectsData from "../data/projects.json";
import MediaDisplay from "./MediaDisplay";
import ReadMoreTag, { useReadMoreHandlers } from "./ReadMoreTag";
import ProjectSkillTags from "./ProjectSkillTags";
import IconsProvider from "./MaterialSymbols";

// Memoized component for project description
const ProjectDescription = React.memo(({ description }) => {
  return (
    <>
      <p className={styles.description}>{description?.intro}</p>
      {description?.description && (
        <div className={styles.expandedContent}>
          <ul>
            {description.description.map((detail, detailIndex) => (
              <li
                key={detailIndex}
                dangerouslySetInnerHTML={{ __html: detail }}
              />
            ))}
          </ul>
        </div>
      )}
    </>
  );
});

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const { notify, handleMouseEnter, handleMouseLeave } = useReadMoreHandlers();

  useEffect(() => {
    const importAll = (r) => {
      let files = {};
      r.keys().forEach((item) => {
        files[item] = r(item);
      });
      return files;
    };

    // Import all media files from the medias directory
    const mediaFiles = importAll(
      require.context("../medias", true, /\.(png|jpe?g|mp4)$/)
    );

    // Import all description files
    const descriptionFiles = importAll(
      require.context("../medias", true, /description\.json$/)
    );

    // Process the projects data and attach media files and descriptions
    const processedProjects = projectsData.projects.map((project) => {
      const projectMedia = Object.keys(mediaFiles)
        .filter((path) => path.includes(project.mediaFolder))
        .map((path) => ({
          src: mediaFiles[path],
          type: path.endsWith(".mp4") ? "video" : "image",
        }));

      // Find and parse the description file for this project
      const descriptionFile = Object.keys(descriptionFiles).find((path) =>
        path.includes(project.mediaFolder)
      );
      const description = descriptionFile
        ? descriptionFiles[descriptionFile]
        : null;

      return {
        ...project,
        media: projectMedia,
        description: description,
      };
    });

    setProjects(processedProjects);
  }, []);

  const toggleExpand = (projectIndex, event) => {
    event.stopPropagation();
    setExpandedCards((prev) => ({
      ...prev,
      [projectIndex]: !prev[projectIndex],
    }));
  };

  const createRipple = (event) => {
    const rippleContainer = event.currentTarget.querySelector(
      `.${styles.rippleContainer}`
    );
    if (!rippleContainer) return;

    const circle = document.createElement("span");
    const diameter = Math.max(
      rippleContainer.clientWidth,
      rippleContainer.clientHeight
    );
    const radius = diameter / 2;

    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${
      event.clientX - rippleContainer.getBoundingClientRect().left - radius
    }px`;
    circle.style.top = `${
      event.clientY - rippleContainer.getBoundingClientRect().top - radius
    }px`;
    circle.classList.add(styles.ripple);

    // 移除之前的涟漪效果
    const ripple = rippleContainer.getElementsByClassName(styles.ripple)[0];
    if (ripple) {
      ripple.remove();
    }

    rippleContainer.appendChild(circle);

    // 600ms后移除涟漪效果（与动画时长相匹配）
    setTimeout(() => {
      if (circle.parentElement) {
        circle.remove();
      }
    }, 600);
  };

  const handleCardClick = (index, event) => {
    if (!expandedCards[index]) {
      toggleExpand(index, event);
      createRipple(event);
    }
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>My Projects</h2>
      {projects.map((project, index) => (
        <div
          key={index}
          className={`${styles.card} ${
            expandedCards[index] ? styles.expanded : ""
          }`}
          onClick={(e) => handleCardClick(index, e)}
        >
          <div className={styles.rippleContainer}></div>
          <div
            className={`${styles.content} content ${
              expandedCards[index] ? `${styles.expanded} expanded` : ""
            }`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className={styles.contentHeader}>
              <div className={styles.titleContainer}>
                <button
                  className={`${styles.expandButton} ${
                    expandedCards[index] ? styles.expanded : ""
                  }`}
                  onClick={(e) => toggleExpand(index, e)}
                >
                  <IconsProvider>expand_more</IconsProvider>
                </button>
                <h3>{project.title}</h3>
              </div>

              {project.description?.links &&
                project.description.links.length > 0 && (
                  <div className={styles.linksContainer}>
                    {project.description.links.map((link, linkIndex) => (
                      <a
                        key={linkIndex}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkButton}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconsProvider>link</IconsProvider>
                        <span>{link.text}</span>
                      </a>
                    ))}
                  </div>
                )}
            </div>
            <ProjectSkillTags skills={project.skills} />
            <ProjectDescription description={project.description} />
            <ReadMoreTag notify={notify} />
          </div>
          <MediaDisplay project={project} projectIndex={index} />
        </div>
      ))}
    </div>
  );
};

export default Projects;
